import React, {createContext, useContext, useEffect, useState} from 'react';
import {getModuleList} from '../api/module';
import {ModuleDto} from '../api/dto/module';

interface ModuleContextType {
  modules?: ModuleDto[];
  setModules: (modules?: ModuleDto[]) => void;
  refreshModules: () => void;
}

const ModuleContext = createContext<ModuleContextType | undefined>(undefined);

export const ModuleProvider = ({children}: {children: React.ReactNode}) => {
  const platformId = localStorage.getItem('platformId');
  const [modules, setModules] = useState<ModuleDto[] | undefined>(() => {
    const cachedModule = localStorage.getItem('modules');
    return cachedModule ? JSON.parse(cachedModule) : undefined;
  });

  const fetchModules = async () => {
    if (!platformId) return;
    try {
      const response = await getModuleList({limit: 100});
      if (response?.data?.items) {
        setModules(response.data.items);
        localStorage.setItem('modules', JSON.stringify(response.data.items));
      }
    } catch (error) {
      setModules(undefined);
      localStorage.removeItem('modules');
    }
  };

  useEffect(() => {
    if (!modules && platformId) {
      fetchModules();
    }

    const interval = setInterval(() => {
      fetchModules();
    }, 24 * 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, [platformId]);

  const refreshModules = () => fetchModules();

  return (
    <ModuleContext.Provider value={{modules, setModules, refreshModules}}>
      {children}
    </ModuleContext.Provider>
  );
};

export const useModule = () => {
  const context = useContext(ModuleContext);
  if (!context) {
    throw new Error('useModule must be used within a ModuleProvider');
  }
  return context;
};
